import { Box, Divider, Drawer, IconButton, Link as MuiLink, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionButton,
  ChipEmailRecipientField,
  FieldInput,
  HorizontalProgressBar,
  IconClose,
  validateEmailAddress,
} from "design-components";
import {
  BOOKING_DELETE_MESSAGE_MAX_LENGTH,
  BOOKING_DELETE_REASON_MAX_LENGTH,
  buildBookingDeleteDefaultMessage,
  formatDeletePreviewCarrierQuantity,
} from "./booking-delete.constants";
import type {
  BookingDeleteCarrierAssignment,
  BookingDeleteDrawerProps,
} from "./booking-delete-drawer.types";

export function BookingDeleteDrawer(props: BookingDeleteDrawerProps) {
  const {
    open,
    onClose,
    booking,
    carrierAssignments,
    canNotifyCarrier = false,
    containerQuantity = null,
    isMultipiece = false,
    initialRecipients: initialRecipientsProp,
    initialMessageBody: initialMessageBodyProp,
    loading = false,
    submitError = null,
    onClearSubmitError,
    onConfirm,
  } = props;

  const hasCarriers = carrierAssignments.length > 0;
  const showEmailSection = hasCarriers && canNotifyCarrier;

  const [recipients, setRecipients] = useState<string[]>(() =>
    initialRecipientsProp ?? initialRecipientsFromAssignments(carrierAssignments),
  );
  const [messageBody, setMessageBody] = useState(() =>
    initialMessageBodyProp ?? (booking ? buildBookingDeleteDefaultMessage(booking) : ""),
  );
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const messageBodySnapshotRef = useRef("");

  const onClearSubmitErrorRef = useRef(onClearSubmitError);
  onClearSubmitErrorRef.current = onClearSubmitError;

  const clearErrors = useCallback(() => {
    setFormError(null);
    onClearSubmitErrorRef.current?.();
  }, []);

  const messageRequired = showEmailSection && recipients.length > 0;
  const messageFieldDisabled = showEmailSection && recipients.length === 0;
  const messageFieldError = messageRequired && !messageBody.trim();

  const reasonTooLong = reason.length > BOOKING_DELETE_REASON_MAX_LENGTH;
  const bookingId = booking?.bookingId;

  const deleteDisabled = useMemo(() => {
    if (submitting || loading || !booking) return true;
    if (messageRequired && !messageBody.trim()) return true;
    if (reasonTooLong) return true;
    return false;
  }, [booking, loading, messageBody, messageRequired, reasonTooLong, submitting]);

  // Reset reason / submit state only when the drawer opens for a booking — not on
  // parent re-renders (e.g. Clerk session refresh) or preview prop identity churn.
  useEffect(() => {
    if (!open || bookingId == null) return;
    setReason("");
    setFormError(null);
    onClearSubmitErrorRef.current?.();
    setSubmitting(false);
  }, [open, bookingId]);

  // Apply preview recipients/message when delete-preview data arrives.
  useEffect(() => {
    if (!open || !booking) return;
    const nextRecipients = initialRecipientsProp ?? initialRecipientsFromAssignments(carrierAssignments);
    const defaultMessage =
      initialMessageBodyProp ?? buildBookingDeleteDefaultMessage(booking);
    messageBodySnapshotRef.current = defaultMessage;
    setRecipients(nextRecipients);
    setMessageBody(nextRecipients.length > 0 ? defaultMessage : "");
  }, [
    open,
    booking,
    carrierAssignments,
    initialRecipientsProp,
    initialMessageBodyProp,
  ]);

  useEffect(() => {
    if (recipients.length > 0 && messageBody.trim()) {
      messageBodySnapshotRef.current = messageBody;
    }
  }, [messageBody, recipients.length]);

  const handleRecipientsChange = useCallback(
    (next: string[]) => {
      const hadRecipients = recipients.length > 0;
      const hasRecipients = next.length > 0;

      if (!hasRecipients && hadRecipients) {
        setMessageBody((current) => {
          if (current.trim()) {
            messageBodySnapshotRef.current = current;
          }
          return current;
        });
      } else if (hasRecipients && !hadRecipients) {
        setMessageBody(messageBodySnapshotRef.current);
      }

      setRecipients(next);
      clearErrors();
    },
    [clearErrors, recipients],
  );

  const handleClose = useCallback(() => {
    if (submitting || loading) return;
    clearErrors();
    onClose();
  }, [clearErrors, loading, onClose, submitting]);

  const handleConfirm = useCallback(async () => {
    if (!booking || deleteDisabled) return;

    if (showEmailSection && recipients.length > 0) {
      const seen = new Set<string>();
      for (const r of recipients) {
        const t = r.trim();
        const err = validateEmailAddress(t, { required: true, minLen: 3, maxLen: 100 });
        if (err) {
          setFormError("Remove or fix invalid email addresses.");
          return;
        }
        const k = t.toLowerCase();
        if (seen.has(k)) {
          setFormError("Remove duplicate email addresses.");
          return;
        }
        seen.add(k);
      }
    }

    if (messageRequired && !messageBody.trim()) {
      setFormError("Message is required when at least one email is added.");
      return;
    }

    if (showEmailSection && messageBody.length > BOOKING_DELETE_MESSAGE_MAX_LENGTH) {
      setFormError(`Message must be at most ${BOOKING_DELETE_MESSAGE_MAX_LENGTH} characters.`);
      return;
    }

    if (reasonTooLong) {
      setFormError(`Reason must be at most ${BOOKING_DELETE_REASON_MAX_LENGTH} characters.`);
      return;
    }

    const trimmedReason = reason.trim();

    clearErrors();
    setSubmitting(true);
    try {
      await onConfirm({
        bookingId: booking.bookingId,
        recipients: showEmailSection ? [...recipients] : [],
        messageBody: showEmailSection ? messageBody : "",
        reason: trimmedReason ? trimmedReason : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    booking,
    deleteDisabled,
    messageBody,
    messageRequired,
    onConfirm,
    reason,
    reasonTooLong,
    recipients,
    clearErrors,
    showEmailSection,
  ]);

  const bookingNumber = booking?.bookingNumber?.trim() || "—";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgb(255 255 255 / 50%)" },
        },
        paper: {
          className: "user-form-drawer__paper",
        },
      }}
    >
      <Box className="user-form-drawer booking-delete-drawer">
        <Stack spacing={0} className="user-form-drawer__body" sx={{ height: "100%" }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            className="user-form-drawer__header-block"
          >
            <Typography className="user-form-drawer__title" component="h2">
              Delete {bookingNumber}
            </Typography>
            <IconButton type="button" onClick={handleClose} aria-label="Close" className="user-form-drawer__close">
              <IconClose aria-hidden size={24} />
            </IconButton>
          </Stack>

          <Box className="user-form-drawer__fields-scroll">
            <Stack spacing={3} className="user-form-drawer__fields">
              {loading ? (
                <HorizontalProgressBar variant="indeterminate" width="100%" height={4} />
              ) : null}
              {booking && !loading ? (
                <>
                  {hasCarriers ? (
                    <Stack spacing={2} className="booking-delete-drawer__carrier-block">
                      <Typography
                        variant="subtitle1"
                        component="h3"
                        className="booking-delete-drawer__carriers-title"
                      >
                        Carrier(s)
                      </Typography>

                      <Box className="booking-delete-drawer__carriers-table" role="table" aria-label="Assigned carriers">
                        <Box className="booking-delete-drawer__carriers-header" role="row">
                          <Typography
                            variant="body2"
                            component="span"
                            role="columnheader"
                            className="booking-delete-drawer__carriers-header-cell booking-delete-drawer__carriers-header-cell--carrier"
                          >
                            Carrier
                          </Typography>
                          <Typography
                            variant="body2"
                            component="span"
                            role="columnheader"
                            className="booking-delete-drawer__carriers-header-cell booking-delete-drawer__carriers-header-cell--quantity"
                          >
                            Quantity
                          </Typography>
                          <Typography
                            variant="body2"
                            component="span"
                            role="columnheader"
                            className="booking-delete-drawer__carriers-header-cell booking-delete-drawer__carriers-header-cell--contact"
                          >
                            Contact
                          </Typography>
                        </Box>

                        <Stack spacing={1} className="booking-delete-drawer__carriers-rows" role="rowgroup">
                          {carrierAssignments.map((assignment) => (
                            <CarrierAssignmentRow
                              key={assignment.carrierId}
                              assignment={assignment}
                              containerQuantity={containerQuantity}
                              isMultipiece={isMultipiece}
                            />
                          ))}
                        </Stack>
                      </Box>

                      {showEmailSection ? (
                        <>
                          <Divider className="booking-delete-drawer__section-divider" />
                          <Stack spacing={2}>
                            <ChipEmailRecipientField
                              resetSignal={booking.bookingId}
                              label="Send email to"
                              chipAppearance="primarySolid"
                              emails={recipients}
                              onEmailsChange={handleRecipientsChange}
                              duplicateEmailErrorText="Email already exist"
                              helperText={
                                <>
                                  Press enter after each
                                  <br />
                                  Each recipient will receive a separate email
                                </>
                              }
                              placeholder="Add email"
                            />
                            <FieldInput
                              label="Message"
                              required={messageRequired}
                              disabled={messageFieldDisabled}
                              error={messageFieldError}
                              helperText={
                                messageFieldError ? "Required when recipients are added." : undefined
                              }
                              fullWidth
                              multiline
                              minRows={5}
                              outlinedInputProps={{
                                value: messageBody,
                                inputProps: { maxLength: BOOKING_DELETE_MESSAGE_MAX_LENGTH },
                                onChange: (e) => {
                                  const v = e.target.value.slice(0, BOOKING_DELETE_MESSAGE_MAX_LENGTH);
                                  setMessageBody(v);
                                  messageBodySnapshotRef.current = v;
                                  clearErrors();
                                },
                              }}
                            />
                          </Stack>
                        </>
                      ) : null}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" component="p">
                      No carrier is assigned. The booking will be deleted without email notification.
                    </Typography>
                  )}

                  <FieldInput
                    label="Reason"
                    fullWidth
                    multiline
                    minRows={3}
                    outlinedInputProps={{
                      value: reason,
                      inputProps: { maxLength: BOOKING_DELETE_REASON_MAX_LENGTH },
                      onChange: (e) => {
                        const v = e.target.value.slice(0, BOOKING_DELETE_REASON_MAX_LENGTH);
                        setReason(v);
                        clearErrors();
                      },
                    }}
                  />

                  {formError || submitError ? (
                    <Typography variant="body2" color="error" role="alert">
                      {formError ?? submitError}
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1.5} className="booking-delete-drawer__footer">
            <ActionButton
              type="button"
              actionVariant="contained-error"
              size="small"
              onClick={() => void handleConfirm()}
              disabled={deleteDisabled}
            >
              Delete
            </ActionButton>
            <ActionButton
              type="button"
              actionVariant="neutral"
              size="small"
              onClick={handleClose}
              disabled={submitting || loading}
            >
              Cancel
            </ActionButton>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

function CarrierAssignmentRow({
  assignment,
  containerQuantity,
  isMultipiece,
}: {
  assignment: BookingDeleteCarrierAssignment;
  containerQuantity: number | null;
  isMultipiece: boolean;
}) {
  const quantityLabel = formatDeletePreviewCarrierQuantity(assignment.assignedQuantity, {
    containerQuantity,
    isMultipiece,
  });
  const contactName = assignment.primaryContactName.trim() || "—";
  const phone = assignment.phone?.trim() ?? "";

  return (
    <Box className="booking-delete-drawer__carrier-row" role="row">
      <Typography
        variant="body2"
        component="span"
        role="cell"
        className="booking-delete-drawer__carrier-row-cell booking-delete-drawer__carrier-row-cell--carrier booking-delete-drawer__carrier-name"
      >
        {assignment.name.trim() || "—"}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        role="cell"
        className="booking-delete-drawer__carrier-row-cell booking-delete-drawer__carrier-row-cell--quantity"
      >
        {quantityLabel}
      </Typography>
      <Box
        role="cell"
        className="booking-delete-drawer__carrier-row-cell booking-delete-drawer__carrier-row-cell--contact booking-delete-drawer__carrier-contact"
      >
        <Typography variant="body2" component="span" className="booking-delete-drawer__carrier-contact-name">
          {contactName}
        </Typography>
        {phone ? (
          <MuiLink
            href={`tel:${sanitizeTelHref(phone)}`}
            underline="hover"
            className="booking-delete-drawer__carrier-phone-link"
          >
            {phone}
          </MuiLink>
        ) : null}
      </Box>
    </Box>
  );
}

function initialRecipientsFromAssignments(
  carrierAssignments: BookingDeleteCarrierAssignment[],
): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const assignment of carrierAssignments) {
    const email = assignment.email?.trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push(email);
  }
  return emails;
}

function sanitizeTelHref(phone: string): string {
  return phone.replace(/[^\d+]/g, "");
}
